import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { readResponse } from '../utils/fetchSafe';

const SetupPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [valid, setValid] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!token || !email) {
      setValid(false);
      return;
    }
    fetch(`${API_URL}/api/auth/verify-setup-token?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`)
      .then(res => { setValid(res.ok); })
      .catch(() => setValid(false));
  }, [token, email, API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/setup-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, password })
      });
      if (!res.ok) {
        const { data } = await readResponse(res);
        throw new Error(data.detail || 'Failed to set password');
      }
      toast.success('Password created successfully! You can now log in.');
      navigate('/');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (valid === null) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">Verifying...</div>
      </div>
    );
  }

  if (valid === false) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded shadow p-8 max-w-md w-full text-center">
          <h1 className="text-2xl text-[#5B8DB8] mb-4">Invalid or Expired Link</h1>
          <p className="text-gray-600 mb-6">This password setup link is invalid or has already been used. Please contact your administrator for a new invitation.</p>
          <button onClick={() => navigate('/')} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-6 py-2 rounded" data-testid="go-to-login-btn">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white rounded shadow p-8 max-w-md w-full">
        <h1 className="text-2xl text-[#5B8DB8] mb-2">Create Your Password</h1>
        <p className="text-gray-500 text-sm mb-6">Set up your password for <strong>{email}</strong></p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="At least 6 characters"
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="Re-enter your password"
              data-testid="confirm-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={saving || !password || !confirmPassword}
            className={`w-full py-2 rounded text-white ${saving || !password || !confirmPassword ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#4ECFDB] hover:bg-[#3AB8C5]'}`}
            data-testid="setup-password-submit"
          >
            {saving ? 'Setting up...' : 'Create Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SetupPassword;
